<script setup lang="ts">
import { computed, reactive, watch } from 'vue';
import CInput from '@/components/shared/CInput';
import CCheckbox from '@/components/shared/CCheckbox.vue';
import CButton from '@/components/shared/CButton';
import CSelect, { valueReducer, type CSelectItem } from '@/components/shared/CSelect';
import type {
    BaseCanvasElement,
    BaseTextElement,
    BoundedTextElement,
    PathITextElement,
    TextTransformElement,
} from '@/typings/customilyElements/canvasElements';
import type { CanvasEx } from '@/typings/canvas';

const AUTOFILL_OPTIONS: CSelectItem[] = [
    { value: 'ORDER_NUMBER', label: 'Order Number' },
    { value: 'FILENAME', label: 'File Name' },
];

const DATE_FORMAT_OPTIONS: CSelectItem[] = [
    { value: 'MM-DD-YY', label: '10-14-25' },
    { value: 'MM.DD.YYYY', label: '10.14.2025' },
    { value: 'MM.DD.YY', label: '10.14.25' },
    { value: 'DD/MM/YYYY', label: '14/10/2025' },
    { value: 'DD-MM-YY', label: '14-10-25' },
    { value: 'DD.MM.YYYY', label: '14.10.2025' },
    { value: 'DD.MM.YY', label: '14.10.25' },
    { value: 'YYYY-MM-DD', label: '2025-10-14' },
    { value: 'DD MMM YYYY', label: '14 Oct 2025' },
    { value: 'MMM D, YYYY', label: 'Oct 14, 2025' },
    { value: 'YYYY MMM D', label: '2025 Oct 14' },
    { value: 'DD-MMM-YYYY', label: '14-Oct-2025' },
    { value: 'DD MMMM YYYY', label: '14 October 2025' },
    { value: 'MMMM D, YYYY', label: 'October 14, 2025' },
    { value: 'YYYY MMMM D', label: '2025 October 14' },
    { value: 'dddd, MMMM D, YYYY', label: 'Tuesday, October 14, 2025' },
];

const DATE_LANG_WARNING_FORMATS = new Set([
    'DD MMM YYYY', 'MMM D, YYYY', 'YYYY MMM D', 'DD-MMM-YYYY',
    'DD MMMM YYYY', 'MMMM D, YYYY', 'YYYY MMMM D', 'dddd, MMMM D, YYYY',
]);

const props = defineProps<{
    canvas: CanvasEx;
    textElement?: BoundedTextElement | TextTransformElement | PathITextElement;
    editMode?: boolean;
    exportFormat?: string;
    melcoTextIds?: string[];
    forDesignTemplateCreation?: boolean;
    isTemplateCreation?: boolean;
}>();

const emit = defineEmits<{
    (e: 'element-props-updated', value: string): void;
    (e: 'use-as-mask-change', value: boolean): void;
    (e: 'use-as-cutout-change', value: boolean): void;
    (e: 'allow-edit-change'): void;
    (e: 'autofill-toggle'): void;
    (e: 'adjust-text-position'): void;
}>();

const model = reactive({
    prefix: '',
    suffix: '',
    betweenCharacters: '',
    betweenWords: '',
    referenceId: '',
    allowEditChecked: false,
    useAsMask: false,
    useAsCutout: false,
    autofillSource: '',
    dateFormat: '',
});

const isBoundedText = computed(() => props.textElement?.type === 'bounded-text');
const hasTextPathDraw = computed(
    () => isBoundedText.value && !!(props.textElement as BoundedTextElement)?.textPath
);
const isOFM = computed(() => props.exportFormat === 'ofm');

const melcoOptions = computed<CSelectItem[]>(() =>
    (props.melcoTextIds || []).map((id) => ({ value: id, label: id }))
);

const showDateLangWarning = computed(
    () => !!model.dateFormat && DATE_LANG_WARNING_FORMATS.has(model.dateFormat)
);

const initModel = (element: BoundedTextElement | TextTransformElement | PathITextElement) => {
    const el = element as BaseTextElement;
    model.prefix = el.prefix || '';
    model.suffix = el.suffix || '';
    model.betweenCharacters = el.betweenCharacters || '';
    model.betweenWords = el.betweenWords || '';
    model.referenceId = el.referenceId || '';
    model.allowEditChecked = el.allowEdit || el.oldAllowEdit || false;
    model.autofillSource = el.autofillSource || '';

    if (element.type === 'bounded-text') {
        const bounded = element as BoundedTextElement;
        model.useAsMask = bounded.useAsMask || false;
        model.useAsCutout = bounded.useAsCutout || false;
        const df = bounded.dateFormat as unknown as string | { format: string };
        model.dateFormat = df && typeof df === 'object' ? df.format || '' : df || '';
    } else {
        model.useAsMask = false;
        model.useAsCutout = false;
        model.dateFormat = '';
    }
};

watch(
    () => props.textElement,
    (element) => {
        if (!element) return;
        initModel(element);
    },
    { immediate: true }
);

const updateElementProp = (key: string, value: unknown) => {
    const element = props.textElement as BaseTextElement;
    if (!element) return;
    if (props.editMode) {
        element.setEditedProp(key);
    }
    element.set(key as keyof BaseCanvasElement, value);
    if (element.fire) {
        element.fire('modified');
    }
    props.canvas.requestRenderAll();
    emit('element-props-updated', element.uuid as string);
};

const updateDateFormat = () => {
    updateElementProp('dateFormat', model.dateFormat ? { format: model.dateFormat } : null);
};

const handleAutofillSourceChange = (value: unknown) => {
    model.autofillSource = value as string;
    updateElementProp('autofillSource', value);
};

const handleReferenceIdChange = (value: unknown) => {
    model.referenceId = value as string;
    updateElementProp('referenceId', value);
};

const handleDateFormatChange = (value: unknown) => {
    model.dateFormat = (value as string) || '';
    updateDateFormat();
};

const handleAllowEditChange = () => {
    const newValue = !model.allowEditChecked;
    model.allowEditChecked = newValue;
    if (newValue) {
        model.autofillSource = '';
    }
    emit('allow-edit-change');
};

const handleUseAsMaskChange = (value: boolean) => {
    model.useAsMask = value;
    if (value) {
        model.useAsCutout = false;
        model.autofillSource = '';
    }
    emit('use-as-mask-change', value);
};

const handleUseAsCutoutChange = (value: boolean) => {
    model.useAsCutout = value;
    if (value) {
        model.useAsMask = false;
        model.autofillSource = '';
    }
    emit('use-as-cutout-change', value);
};

const handleAutofillToggle = () => {
    if (model.autofillSource) {
        model.autofillSource = '';
    } else {
        model.autofillSource = 'ORDER_NUMBER';
        model.useAsMask = false;
        model.useAsCutout = false;
        model.allowEditChecked = false;
    }
    emit('autofill-toggle');
};
</script>

<template>
    <div class="text-advanced-wrapper">
        <!-- Fixed Words & Symbols -->
        <div class="section-title">Fixed Words &amp; Symbols</div>
        <hr class="section-divider" />
        <div class="row">
            <div class="col-6">
                <c-input
                    v-model="model.prefix"
                    label="Prefix"
                    placeholder="Example: Est. [Text]"
                    :trim="false"
                    @input="updateElementProp('prefix', $event)"
                />
            </div>
            <div class="col-6">
                <c-input
                    v-model="model.suffix"
                    label="Suffix"
                    placeholder="Example: [Text] Trip"
                    :trim="false"
                    @input="updateElementProp('suffix', $event)"
                />
            </div>
        </div>
        <div class="row">
            <div class="col-6">
                <c-input
                    v-model="model.betweenCharacters"
                    label="Between Characters"
                    placeholder="Example: T·E·X·T"
                    :trim="false"
                    @input="updateElementProp('betweenCharacters', $event)"
                />
            </div>
            <div class="col-6">
                <c-input
                    v-model="model.betweenWords"
                    label="Between Words"
                    placeholder="Example: Text ❤︎⁠ Text"
                    :trim="false"
                    @input="updateElementProp('betweenWords', $event)"
                />
            </div>
        </div>

        <div v-if="isOFM" class="row">
            <div class="col-6">
                <c-select
                    :value="model.referenceId"
                    label="Reference ID"
                    :options="melcoOptions"
                    :model-transform="valueReducer"
                    @input="handleReferenceIdChange($event)"
                />
            </div>
        </div>

        <template v-if="isBoundedText">
            <!-- Others -->
            <div class="section-title">Others</div>
            <hr class="section-divider" />
            <div class="row align-items-center">
                <div v-if="isTemplateCreation && !hasTextPathDraw" class="col-6">
                    <c-checkbox
                        :value="model.allowEditChecked"
                        label="Allow Moving Text"
                        :disabled="!!model.autofillSource"
                        @input="handleAllowEditChange"
                    />
                </div>
                <div v-if="!forDesignTemplateCreation && !hasTextPathDraw" class="col-6">
                    <c-checkbox
                        :value="!!model.autofillSource"
                        label="Autofilled"
                        :disabled="model.useAsMask || model.useAsCutout || model.allowEditChecked"
                        @input="handleAutofillToggle"
                    />
                </div>
                <div class="col-6">
                    <c-checkbox
                        :value="model.useAsMask"
                        label="Use as Mask"
                        :disabled="model.useAsCutout || !!textElement?.locked || !!model.autofillSource"
                        @input="handleUseAsMaskChange($event)"
                    />
                </div>
                <div v-if="!hasTextPathDraw" class="col-6">
                    <c-checkbox
                        :value="model.useAsCutout"
                        label="Use as Cutout"
                        :disabled="model.useAsMask || !!textElement?.locked || !!model.autofillSource"
                        @input="handleUseAsCutoutChange($event)"
                    />
                </div>
            </div>
        </template>

        <div v-if="isBoundedText && !hasTextPathDraw" class="row">
            <div class="col-6">
                <c-select
                    :value="model.dateFormat"
                    label="Date Format"
                    :options="DATE_FORMAT_OPTIONS"
                    :model-transform="valueReducer"
                    :clearable="true"
                    @input="handleDateFormatChange($event)"
                />
                <small v-if="showDateLangWarning">The month/day will be displayed in English</small>
            </div>
            <div v-if="!forDesignTemplateCreation && model.autofillSource" class="col-6">
                <c-select
                    :value="model.autofillSource"
                    label="Autofilled Values"
                    :options="AUTOFILL_OPTIONS"
                    :model-transform="valueReducer"
                    @input="handleAutofillSourceChange($event)"
                />
            </div>
        </div>

        <div v-if="isBoundedText && !forDesignTemplateCreation && model.allowEditChecked" class="row mt-2">
            <div class="col-auto">
                <c-button variant="secondary" text="Adjust text position" @click="emit('adjust-text-position')" />
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.text-advanced-wrapper {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;

    .section-title {
        font-weight: 400;
        font-size: 0.875rem;
    }

    .section-divider {
        margin: 0;
    }
}
</style>
